// Telemetry theme tracker. Publishes what happens on the page as Shopify custom events;
// the Telemetry custom pixel receives them and applies the consent rules before anything leaves the browser.
// Config comes from window.peTelemetry (set in layout/theme.liquid). No cookies. The only storage is the chat's
// visit summary in sessionStorage, kept only with Preferences consent (see below).
(function () {
	'use strict';

	const config = window.peTelemetry;
	if (!config || !config.store_key) return;

	const INTERACTIVE = 'a[href], button, input, select, textarea, label, summary, [role="button"], [on\\:click], [onclick]';
	const page = { shownAt: null, errors: 0, scroll: new Set() };

	function publish(name, data) {
		note(name, data);
		try {
			window.Shopify.analytics.publish(name, data);
		} catch (error) {
			/* analytics must never break the page */
		}
	}

	// ---------- Chat API: window.PETelemetry.send(name, props) for the sales assistant widget, which lives in a shadow root
	// the click listener cannot see. It goes through publish() like every other event, so the pixel applies the same
	// visit, consent tier, device and filters. Only pe:chat_ names, and only short strings, numbers and booleans in props.

	const CHAT_NAME = /^pe:chat_[a-z0-9_]{1,50}$/;
	const CHAT_KEY = /^[a-z0-9_]{1,40}$/;
	const CHAT_TEXT_MAX = 64;
	function send(name, props) {
		if (typeof name !== 'string' || !CHAT_NAME.test(name)) return false;
		const clean = {};
		if (props && typeof props === 'object') {
			for (const key of Object.keys(props).slice(0, 12)) {
				const v = props[key];
				const ok = typeof v === 'boolean' || (typeof v === 'number' && isFinite(v)) || (typeof v === 'string' && v.length > 0 && v.length <= CHAT_TEXT_MAX);
				if (CHAT_KEY.test(key) && ok) clean[key] = v;
			}
		}
		publish(name, { props: clean });
		return true;
	}

	// ---------- Visit summary: coarse flags for this visit, only with Preferences consent (Shopify Customer Privacy API).
	// Built from the events publish() already sees. Cleared when consent is withdrawn. Handles and yes/no only:
	// no timestamps or durations are stored or exposed.

	const VISIT_KEY = 'pe:visit';
	const LOW_BAND_MAX = 500; // the Help me choose "Up to £500" band: a price filter maximum of £500 or less (inclusive)
	const REPEAT_VIEWS = 3; // views of one product that count as "back several times"

	function preferences() {
		const cp = privacy();
		const c = cp && cp.currentVisitorConsent ? cp.currentVisitorConsent() : {};
		return c.preferences || '';
	}
	function clearVisit() {
		try {
			sessionStorage.removeItem(VISIT_KEY);
		} catch (error) {
			/* storage blocked */
		}
	}
	function readVisit() {
		try {
			return JSON.parse(sessionStorage.getItem(VISIT_KEY)) || {};
		} catch (error) {
			return {};
		}
	}
	function mark(change) {
		if (preferences() !== 'yes') return;
		try {
			const before = sessionStorage.getItem(VISIT_KEY);
			const v = readVisit();
			change(v);
			const after = JSON.stringify(v);
			if (after !== before) sessionStorage.setItem(VISIT_KEY, after); // most events change nothing: no write
		} catch (error) {
			/* the summary must never break the page */
		}
	}
	function handleOf(path) {
		if (config.page_type !== 'product') return null;
		const m = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/([a-z0-9-]{1,80})\/?$/i.exec(path);
		return m ? m[1].toLowerCase() : null;
	}
	function lowBand(max) {
		const n = parseFloat(String(max).replace(/,/g, '')); // the theme prints prices like 1,000.00
		return n > 0 && n <= LOW_BAND_MAX;
	}

	// Called from publish() for every event; reads the same props the pixel gets.
	function note(name, data) {
		try {
			const p = (data && data.props) || {};
			const text = String(p.label || p.title || '');
			if (name === 'pe:accordion') {
				mark((v) => {
					if (/deliver|shipping|return|refund/i.test(text)) v.d = true;
					if (/review/i.test(text)) v.r = true;
					if (/spec|technical|dimension/i.test(text)) v.s = true;
					if (/finance|klarna|clearpay|afterpay|monthly|pay over/i.test(text)) v.f = true;
				});
			} else if (name === 'pe:click') {
				mark((v) => {
					const type = data && data.element_type;
					// blocks/product-finance.liquid and blocks/review.liquid (the button that opens the reviews drawer) carry these data-pe-el types
					if (type === 'finance-popup' || type === 'product-finance' || /klarna|clearpay|afterpay/i.test(text)) v.f = true;
					if (type === 'review') v.r = true;
					if (/deliver|shipping|returns?\b|refund/i.test(text)) v.d = true;
				});
			} else if (name === 'pe:filter') {
				mark((v) => {
					if ((p.filter === 'sort_by' && p.value === 'price-ascending') || (p.filter === 'filter.v.price.lte' && lowBand(p.value))) v.l = true;
				});
			}
		} catch (error) {
			/* the summary must never break the page */
		}
	}

	// Once per page load, as soon as consent is known: models viewed, repeat views, price sort in the address, campaign landing.
	let pageNoted = false;
	function notePage() {
		try {
			if (pageNoted || preferences() !== 'yes') return;
			pageNoted = true;
			const handle = handleOf(location.pathname);
			const qs = new URLSearchParams(location.search);
			mark((v) => {
				const first = !v.seen;
				v.seen = true;
				if (qs.get('sort_by') === 'price-ascending' || lowBand(qs.get('filter.v.price.lte'))) v.l = true;
				if (!handle) return;
				v.m = (v.m || []).filter((h) => h !== handle).concat(handle).slice(-6);
				const n = Object.assign(Object.create(null), v.n); // handles are page text: no prototype to pollute
				n[handle] = (n[handle] || 0) + 1;
				v.n = {};
				for (const h of v.m) if (n[h]) v.n[h] = n[h]; // only the models still listed, so it cannot grow
				if (first && Array.from(qs.keys()).some((k) => /^(utm_|gclid$|fbclid$)/.test(k))) v.c = handle;
			});
			// A long stay on a product page counts like opening the specs. Only the yes/no is kept.
			if (handle) setTimeout(() => document.visibilityState === 'visible' && mark((v) => (v.s = true)), 120000);
		} catch (error) {
			/* the summary must never break the page */
		}
	}
	// The theme's own cart event (assets/events.js). The product form says when something was added (failed adds don't count).
	// A removal is the cart page or drawer reporting fewer items than before, after an add this visit.
	// itemCount is the cart total on cart-items-component updates. The count before the first one is read from the header bubble.
	function bubbleCount() {
		const el = document.querySelector('[ref="cartBubbleCount"]');
		const n = el ? parseInt(el.textContent, 10) : NaN;
		return isNaN(n) ? null : n;
	}
	document.addEventListener('cart:update', (event) => {
		try {
			const d = (event.detail && event.detail.data) || {};
			if (d.source === 'product-form-component' && !d.didError) {
				mark((v) => (v.a = true));
				setTimeout(() => mark((v) => (v.q = bubbleCount())), 0);
			} else if (d.source === 'cart-items-component' && !d.didError && typeof d.itemCount === 'number') {
				mark((v) => {
					if (v.a && typeof v.q === 'number' && d.itemCount < v.q) v.x = true;
					v.q = d.itemCount;
				});
			}
		} catch (error) {
			/* the summary must never break the page */
		}
	});
	document.addEventListener('visitorConsentCollected', (event) => {
		if (event.detail && !event.detail.preferencesAllowed) clearVisit();
		else notePage();
	});

	function visitSummary() {
		const consent = preferences();
		if (consent === 'no') clearVisit();
		if (consent !== 'yes') return null;
		const v = readVisit();
		const repeat = Object.keys(v.n || {}).find((h) => v.n[h] >= REPEAT_VIEWS);
		return Object.freeze({
			finance: !!v.f,
			delivery_returns: !!v.d,
			reviews: !!v.r,
			specs_or_long_view: !!v.s,
			models_viewed: Object.freeze((v.m || []).slice()),
			lowest_price: !!v.l,
			repeat_product: repeat || null,
			added_then_removed: !!v.x,
			landing_model: v.c || null,
		});
	}

	// Locked: nothing can replace or edit it. If something else already defined the name, ours is not exposed.
	try {
		if (!Object.prototype.hasOwnProperty.call(window, 'PETelemetry')) {
			Object.defineProperty(window, 'PETelemetry', { value: Object.freeze({ send, visitSummary }), writable: false, configurable: false });
		}
	} catch (error) {
		/* not exposed */
	}

	// ---------- Page context and consent

	function privacy() {
		return window.Shopify && window.Shopify.customerPrivacy;
	}

	function publishContext() {
		const cp = privacy();
		const consent = cp && cp.currentVisitorConsent ? cp.currentVisitorConsent() : {};
		publish('pe:context', {
			store_key: config.store_key,
			template: config.template,
			page_type: config.page_type,
			theme_id: window.Shopify && window.Shopify.theme ? String(window.Shopify.theme.id) : null,
			market: config.market,
			title: config.title,
			region: cp && cp.getRegion ? cp.getRegion() : '',
			consent_analytics: consent.analytics || '',
		});
	}

	function tally(kind, extra) {
		const cp = privacy();
		publish(kind === 'shown' ? 'pe:consent_shown' : 'pe:consent_choice', {
			kind,
			page_type: config.page_type,
			country: cp && cp.getRegion ? cp.getRegion().slice(0, 2) : '',
			...extra,
		});
	}

	function decideBucket() {
		if (!page.shownAt) return 'n/a';
		const s = (Date.now() - page.shownAt) / 1000;
		return s < 5 ? '<5s' : s < 30 ? '5-30s' : s < 120 ? '30s-2m' : '2m+';
	}

	function startConsent() {
		const cp = privacy();
		// shouldShowBanner() is true for every visitor in a consent region, so also require "no choice yet".
		const c = cp && cp.currentVisitorConsent ? cp.currentVisitorConsent() : {};
		const undecided = !c.analytics && !c.marketing && !c.preferences;
		if (cp && cp.shouldShowBanner && cp.shouldShowBanner() && undecided) {
			page.shownAt = Date.now();
			tally('shown', { source: 'banner' });
		}
		publishContext();
	}

	document.addEventListener('visitorConsentCollected', (event) => {
		const d = event.detail || {};
		const all = [d.analyticsAllowed, d.marketingAllowed, d.preferencesAllowed];
		tally(all.every(Boolean) ? 'accept_all' : all.some(Boolean) ? 'custom' : 'reject_all', {
			source: page.shownAt ? 'banner' : 'footer',
			analytics: !!d.analyticsAllowed,
			marketing: !!d.marketingAllowed,
			preferences: !!d.preferencesAllowed,
			decide_bucket: decideBucket(),
		});
		page.shownAt = null;
		publishContext(); // the pixel re-decides the tier from the new choice
	});

	// Shopify's banner often loads the consent API before us: send the context now so quick bounces still count.
	if (privacy() && privacy().getRegion) publishContext();
	if (window.Shopify && window.Shopify.loadFeatures) {
		window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
			if (error) publishContext();
			else startConsent();
			notePage();
		});
	} else {
		publishContext();
	}

	// ---------- Elements: blocks carry data-pe-el="type:id"; sections fall back to their wrapper ID

	function elementOf(node) {
		const block = node.closest && node.closest('[data-pe-el]');
		if (block) {
			const raw = block.getAttribute('data-pe-el');
			const cut = raw.lastIndexOf(':'); // app block types contain colons themselves
			// Theme block IDs look like "AL1lDUXh…__price_P6wAYq-4": keep the template key, drop the per-render suffix.
			const instance = raw.slice(cut + 1).split('__').pop().replace(/-\d+$/, '');
			return { el: block, type: raw.slice(0, cut), instance };
		}
		const section = node.closest && node.closest('.shopify-section');
		if (section) return { el: section, type: 'section', instance: section.id.split('__').pop() };
		// Shopify's cookie banner lives outside the theme's sections
		const banner = node.closest && node.closest('[id^="shopify-pc__"]');
		if (banner) return { el: banner, type: 'cookie-banner', instance: 'cookie-banner' };
		// Third-party widgets outside the sections: Intercom's launcher, app popups (Klaviyo sign-up forms...)
		const finance = node.closest && node.closest('afterpay-modal, afterpay-placement, klarna-placement, [class*="afterpay"], [class*="klarna"], [class*="clearpay"]');
		if (finance) return { el: finance, type: 'finance-popup', instance: finance.tagName.toLowerCase() };
		const intercom = node.closest && node.closest('[class*="intercom-"], #intercom-container');
		if (intercom) return { el: intercom, type: 'intercom', instance: 'launcher' };
		const popup = node.closest && node.closest('[role="dialog"], [aria-modal="true"], dialog');
		if (popup) return { el: popup, type: 'popup', instance: (popup.getAttribute('aria-label') || popup.id || 'popup').slice(0, 60) };
		return null;
	}

	// Position among identical blocks on the page (product cards in a grid share one block ID).
	function positionOf(el) {
		const key = el.getAttribute('data-pe-el');
		if (!key) return undefined;
		const same = document.querySelectorAll(`[data-pe-el="${CSS.escape(key)}"]`);
		return same.length > 1 ? Array.prototype.indexOf.call(same, el) + 1 : undefined;
	}

	function labelOf(node) {
		const target = node.closest(INTERACTIVE) || node;
		if (target.matches('input, textarea, select')) return target.getAttribute('aria-label') || undefined;
		const text = (target.getAttribute('aria-label') || target.innerText || target.textContent || '').replace(/\s+/g, ' ').trim();
		return text.slice(0, 60) || undefined;
	}

	// ---------- Impressions: at least half on screen (or half the screen) for a second, once per element per page.
	// Header, footer and product-card internals are skipped (seen on every page, ~60% of volume); their clicks still count.
	const seen = new Set();
	const timers = new WeakMap();
	const io = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const visible =
					entry.intersectionRatio >= 0.5 || entry.intersectionRect.height >= window.innerHeight * 0.5;
				if (visible && !timers.has(entry.target)) {
					timers.set(
						entry.target,
						setTimeout(() => {
							const info = elementOf(entry.target);
							const key = info && `${info.type}:${info.instance}`;
							if (info && !seen.has(key)) {
								seen.add(key);
								publish('pe:impression', { element_type: info.type, element_instance: info.instance });
							}
							io.unobserve(entry.target);
						}, 1000)
					);
				} else if (!visible && timers.has(entry.target)) {
					clearTimeout(timers.get(entry.target));
					timers.delete(entry.target);
				}
			}
		},
		{ threshold: [0, 0.25, 0.5] }
	);

	const TRACKED = '.shopify-section, [data-pe-el]';
	// Spotlight stories report their own impressions (only the active story counts, not the dimmed or parked ones)
	const SKIP = '.shopify-section-group-header-group, .shopify-section-group-footer-group, product-card, .spotlight-story';
	function observeAll(root) {
		const els = Array.from(root.querySelectorAll(TRACKED));
		if (root.matches && root.matches(TRACKED)) els.push(root);
		for (const el of els) {
			// The sticky bar lives in the header group but is worth knowing when it shows
			if (el.closest(SKIP) && !el.closest('sticky-bar')) continue;
			const section = el.classList.contains('shopify-section');
			// Only sections and elements people can act on.
			if (section || el.matches(INTERACTIVE) || el.querySelector(INTERACTIVE)) io.observe(el);
		}
	}

	// ---------- Clicks, with rage (3 within a second) and dead (nothing changed within a second) flags

	let recent = [];
	document.addEventListener(
		'click',
		(event) => {
			if (!event.isTrusted) return; // clicks made by code (e.g. the sticky bar pressing the main Add to cart) aren't people
			const node = event.target instanceof Element ? event.target : null;
			if (!node || node === document.documentElement || node === document.body) return;
			const info = elementOf(node);
			const now = Date.now();
			recent = recent.filter((c) => now - c.t < 1000 && Math.abs(c.x - event.clientX) < 30 && Math.abs(c.y - event.clientY) < 30);
			recent.push({ t: now, x: event.clientX, y: event.clientY });

			const props = { label: labelOf(node), tag: node.tagName.toLowerCase() };
			// Which part of a card was clicked (e.g. Discover cards: card, buy, learn), whatever its label or language
			const action = node.closest('[data-pe-action]');
			if (action) props.action = action.getAttribute('data-pe-action');
			if (info) props.position = positionOf(info.el);
			// Product cards: which card in the grid (1 = top left) and which product, wherever in the card the click lands.
			const card = node.closest('product-card');
			if (card) {
				const scope = card.closest('.shopify-section') || document;
				props.card_position = Array.prototype.indexOf.call(scope.querySelectorAll('product-card'), card) + 1;
				const link = card.querySelector('a[href*="/products/"]');
				if (link) props.product = new URL(link.href).pathname.split('/products/')[1];
				// Grid state when clicked: positions only compare like with like on the default order and no filters
				const qs = new URLSearchParams(location.search);
				props.sort = qs.get('sort_by') || 'default';
				props.filtered = Array.from(qs.keys()).some((k) => k.startsWith('filter.'));
			}
			if (recent.length >= 3) props.rage = true;
			const data = { element_type: info && info.type, element_instance: info && info.instance, props };

			const link = node.closest('a[href]');
			if (link && link.host && link.host !== location.host && /^https?:$/.test(link.protocol)) props.out = link.host.replace(/^www\./, '');
			if (link && !link.getAttribute('href').startsWith('#')) {
				props.nav = true; // the pixel sends this straight away, before the page unloads
				return publish('pe:click', data);
			}
			// Dead click = something that looks clickable (pointer cursor) did nothing. Clicks on plain text aren't dead.
			if (getComputedStyle(node).cursor !== 'pointer') return publish('pe:click', data);
			const url = location.href;
			const clickedAt = Date.now();
			let changed = false;
			const mo = new MutationObserver(() => {
				changed = true;
				mo.disconnect();
			});
			mo.observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });
			setTimeout(() => {
				mo.disconnect();
				if (!changed && reactedAt < clickedAt && location.href === url) props.dead = true;
				publish('pe:click', data);
			}, 1000);
		},
		true
	);

	// Some reactions don't change the HTML (video play/pause, popovers opening or closing): they count as "something happened".
	let reactedAt = 0;
	for (const type of ['play', 'pause', 'toggle', 'loadstart']) document.addEventListener(type, () => (reactedAt = Date.now()), true);

	// Forms sent (newsletter, contact, account...): which form only, never what was typed. Search has its own Shopify event.
	document.addEventListener(
		'submit',
		(event) => {
			const form = event.target instanceof HTMLFormElement ? event.target : null;
			if (!form || /\/search/.test(form.getAttribute('action') || '')) return;
			const info = elementOf(form);
			const type = form.querySelector('input[name="form_type"]');
			publish('pe:form', { element_type: info && info.type, element_instance: info && info.instance, props: { form: (type && type.value) || form.id || 'form', nav: true } });
		},
		true
	);

	// ---------- Scroll depth: one event per 25% milestone reached

	function trackScroll() {
		const h = document.documentElement.scrollHeight;
		const depth = h > 0 ? ((window.scrollY + window.innerHeight) / h) * 100 : 100;
		for (const m of [25, 50, 75, 100]) {
			if (depth >= m - 2 && !page.scroll.has(m)) {
				page.scroll.add(m);
				publish('pe:scroll', { props: { depth: m } });
			}
		}
	}
	let scrollTimer = null;
	window.addEventListener(
		'scroll',
		() => {
			if (!scrollTimer) scrollTimer = setTimeout(() => ((scrollTimer = null), trackScroll()), 250);
		},
		{ passive: true }
	);

	// ---------- Product page and collection interactions

	document.addEventListener('change', (event) => {
		const t = event.target;
		if (!(t instanceof Element)) return;
		const fieldset = t.closest('.variant-option');
		if (fieldset) {
			const legend = fieldset.querySelector('legend');
			const option = legend ? legend.textContent.split(':')[0].trim() : (t.name.match(/options\[(.+)\]/) || [])[1];
			publish('pe:variant_selected', { resource_title: config.title, props: { option, value: t.value } });
		} else if (t.name && (t.name.startsWith('filter.') || t.name === 'sort_by')) {
			publish('pe:filter', { props: { filter: t.name, value: t.type === 'checkbox' && !t.checked ? `-${t.value}` : t.value } });
		}
	});

	// Accordions: a click on a closed <details>' summary (toggle also fires for ones that start open).
	document.addEventListener(
		'click',
		(event) => {
			const summary = event.target instanceof Element && event.target.closest('summary');
			const d = summary && summary.parentElement;
			if (!d || d.tagName !== 'DETAILS' || d.open) return;
			// Icon-only summaries (the mobile menu button) have no text, just an aria-label
			const title = (summary.textContent.replace(/\s+/g, ' ').trim() || summary.getAttribute('aria-label') || '').slice(0, 60);
			publish('pe:accordion', { props: { title, action: 'open' } });
		},
		true
	);
	// play and ended don't bubble; capture catches them. Autoplaying background videos aren't plays,
	// nor are Spotlight's story videos, which the section plays itself as stories advance.
	for (const type of ['play', 'ended']) {
		document.addEventListener(
			type,
			(event) => {
				if (event.target.tagName !== 'VIDEO' || event.target.autoplay || event.target.loop) return;
				if (event.target.closest('spotlight-component')) return;
				const info = elementOf(event.target);
				const title = event.target.getAttribute('aria-label') || event.target.title || (info && info.type);
				publish('pe:video', { props: { title, action: type === 'play' ? 'play' : 'complete' } });
			},
			true
		);
	}

	// ---------- JavaScript errors (max 5 per page; cross-origin "Script error." carries nothing useful)

	// The browser's own view-transition notices arrive as rejections but aren't site errors.
	const NOISE = /^(Script error\.|Skipping view transition|Transition was aborted)/;
	// File and line: the event's own, else the first script URL in the stack (promise rejections only have a stack).
	function sourceOf(file, line, stack) {
		if (file) return `${file.split('?')[0]}${line ? `:${line}` : ''}`;
		const m = String(stack || '').match(/(https?:\/\/[^\s)]+?)(?:\?[^\s):]*)?:(\d+):\d+/);
		return m ? `${m[1]}:${m[2]}` : '';
	}
	function reportError(message, source) {
		if (!message || NOISE.test(String(message)) || page.errors >= 5) return;
		page.errors++;
		publish('pe:error', { props: { message: String(message).slice(0, 200), source: source.slice(-140) } });
	}
	window.addEventListener('error', (e) => reportError(e.message, sourceOf(e.filename, e.lineno, e.error && e.error.stack)));
	window.addEventListener('unhandledrejection', (e) => reportError(e.reason && (e.reason.message || String(e.reason)), sourceOf('', 0, e.reason && e.reason.stack)));

	// ---------- Web vitals: LCP and CLS at the first interaction (or 10 s); INP when the page is hidden

	const vitals = { lcp: null, cls: 0, inp: 0, sent: false };
	function observe(type, cb) {
		try {
			new PerformanceObserver((list) => list.getEntries().forEach(cb)).observe({ type, buffered: true, durationThreshold: 40 });
		} catch (error) {
			/* not supported in this browser */
		}
	}
	observe('largest-contentful-paint', (e) => (vitals.lcp = Math.round(e.startTime)));
	observe('layout-shift', (e) => {
		if (!e.hadRecentInput) vitals.cls += e.value;
	});
	observe('event', (e) => {
		if (e.interactionId) vitals.inp = Math.max(vitals.inp, Math.round(e.duration));
	});
	function sendLoadVitals() {
		if (vitals.sent || vitals.lcp == null) return;
		vitals.sent = true;
		publish('pe:vitals', { props: { lcp: vitals.lcp, cls: Math.round(vitals.cls * 1000) / 1000 } });
	}
	['pointerdown', 'keydown'].forEach((t) => addEventListener(t, sendLoadVitals, { once: true, capture: true }));
	setTimeout(sendLoadVitals, 10000);
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState !== 'hidden') return;
		sendLoadVitals();
		if (vitals.inp) publish('pe:vitals', { props: { inp: vitals.inp } });
		vitals.inp = 0;
	});

	// ---------- Intercom: the widget runs in its own frame, so clicks inside it can't be seen; use its open/close callbacks.
	let intercomTries = 0;
	(function hookIntercom() {
		if (typeof window.Intercom === 'function') {
			window.Intercom('onShow', () => publish('pe:intercom', { props: { action: 'open' } }));
			window.Intercom('onHide', () => publish('pe:intercom', { props: { action: 'close' } }));
		} else if (intercomTries++ < 20) {
			setTimeout(hookIntercom, 1000); // Intercom loads late; give up after 20 s
		}
	})();

	// ---------- Start

	function start() {
		observeAll(document);
		trackScroll(); // short pages reach their milestones without scrolling
		// Sections and blocks added later (filters, quick view, infinite scroll)
		new MutationObserver((records) => {
			for (const r of records) r.addedNodes.forEach((n) => n.nodeType === 1 && observeAll(n));
		}).observe(document.body, { childList: true, subtree: true });
		notePage(); // last, so the summary can never get in the way of the tracking above
	}
	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
	else start();
})();
